import React, { memo, useCallback } from 'react';
import { Chip } from '@mui/material';
import styled from 'styled-components';

import ControlledTextField from 'components/ControlledTextField';
import DeleteIconButton from 'components/DeleteIconButton';
import OptionalMandatoryChooser from 'components/OptionalMandatoryChooser';
import UpDown from 'components/UpDown';
import { Action } from 'state';
import Field from 'types/Field';
import UnitType from 'types/UnitType';
import SplitButton from 'components/SplitButton';

const options = [
  { value: null as unknown as UnitType, label: '-' },
  ...(Object.keys(UnitType) as Array<keyof typeof UnitType>).map((key) => ({
    value: UnitType[key] as UnitType,
    label: UnitType[key],
  })),
];

const StyledFieldForm = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto auto auto;
  grid-template-rows: auto;
  grid-template-areas:
    'upDown label isMandatory fieldType trash'
    '. defaultValue . unitType .';
  align-items: center;
  column-gap: 0.5rem;
  row-gap: 1rem;
`;

const StyledUpDown = styled(UpDown)`
  grid-area: upDown;
`;

interface NumericFieldFormProps {
  field: Field;
  sectionUuid: string;
  dispatch: (action: Action) => void;
}

const NumericFieldForm: React.FC<NumericFieldFormProps> = ({ sectionUuid, field, dispatch }) => {
  const handleOnChangeDefaultValue = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value: defaultValue } = e.target;
      dispatch({ type: 'set-field-default-value', sectionUuid, field, defaultValue });
    },
    [dispatch, sectionUuid, field],
  );

  const handleOnChangeLabel = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const label = e.target.value;
    dispatch({ type: 'set-field-label', sectionUuid, field, label });
  };

  const handleOnChangeOptionalMandatory = (required: boolean) => {
    dispatch({ type: 'set-field-required', sectionUuid, field, required });
  };

  const handleOnClickRemoveField = () => {
    dispatch({ type: 'remove-field', sectionUuid, field });
  };

  const handleOnMoveUp = () => {
    dispatch({ type: 'move-field-up', sectionUuid, field });
  };

  const handleOnMoveDown = () => {
    dispatch({ type: 'move-field-down', sectionUuid, field });
  };

  const handleUnitChange = (key: string | null) => {
    dispatch({ type: 'set-field-measurement-unit', sectionUuid: sectionUuid, field: field, unit: key as UnitType });
  };

  return (
    <StyledFieldForm>
      <StyledUpDown onUp={handleOnMoveUp} onDown={handleOnMoveDown} />
      <ControlledTextField
        label="Field Label"
        size="small"
        fullWidth
        value={field.label}
        style={{ gridArea: 'label' }}
        onChange={handleOnChangeLabel}
      />
      <OptionalMandatoryChooser
        required={field.is_required}
        style={{ gridArea: 'isMandatory' }}
        onChange={handleOnChangeOptionalMandatory}
      />
      <Chip label="Numeric" style={{ gridArea: 'fieldType' }} />
      <DeleteIconButton onClick={handleOnClickRemoveField} style={{ gridArea: 'trash' }} />
      <ControlledTextField
        label="Default value"
        size="small"
        type="number"
        fullWidth
        value={(field.default_value && field.default_value[0]) || ''}
        onChange={handleOnChangeDefaultValue}
        style={{ gridArea: 'defaultValue' }}
      />
      <SplitButton
        options={options}
        optionSelected={field.measurement_unit}
        onOptionChange={handleUnitChange}
        style={{ gridArea: 'unitType' }}
      />
    </StyledFieldForm>
  );
};

export default memo(NumericFieldForm);
